import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { usePartnerAuthStore, getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateEmail(value: string): string {
  return EMAIL_PATTERN.test(value) ? '' : 'Enter a valid email address.';
}

export function ProfilePage() {
  const { partner } = usePartnerAuthStore();

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Profile</h1>
        <p className="text-muted-foreground">Your account details and password.</p>
      </div>

      <div className="card-elevated p-6">
        <h2 className="mb-4 text-base font-semibold leading-none tracking-tight">Your details</h2>
        {/*
          ProtectedRoute always calls fetchMe() on mount, so `partner` can
          arrive, or change identity, after this page first renders. Keying the
          form on the uuid remounts it, so the fields take their values from the
          store during their own first render instead of being seeded once and
          then re-synced by an effect. Keying on the identity rather than on the
          whole partner object means an unrelated store update cannot clobber
          text the user is actively typing.
        */}
        <DetailsForm key={partner?.uuid ?? 'anonymous'} />
      </div>

      <Separator />

      <div className="card-elevated p-6">
        <h2 className="mb-4 text-base font-semibold leading-none tracking-tight">Change password</h2>
        <PasswordForm />
      </div>
    </div>
  );
}

/** Mirrors PartnerProfileController's rules. */
const NAME_MAX = 255;
const EMAIL_MAX = 255;
const PASSWORD_MIN = 8;

function DetailsForm() {
  const { partner, setPartner } = usePartnerAuthStore();

  const [name, setName] = useState(partner?.name ?? '');
  const [email, setEmail] = useState(partner?.email ?? '');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [isSavingDetails, setIsSavingDetails] = useState(false);

  function checkEmail(value: string): string {
    if (!value.trim()) return 'Email is required.';
    if (value.length > EMAIL_MAX) return `Email must be at most ${EMAIL_MAX} characters.`;
    return validateEmail(value);
  }

  async function handleDetailsSubmit(event: FormEvent) {
    event.preventDefault();

    // Both fields are `sometimes` on the API, but this form always sends both,
    // and a partner without a name or email is not a valid account.
    const nextErrors: { name?: string; email?: string } = {};
    if (!name.trim()) nextErrors.name = 'Name is required.';
    else if (name.length > NAME_MAX) nextErrors.name = `Name must be at most ${NAME_MAX} characters.`;
    const emailError = checkEmail(email);
    if (emailError) nextErrors.email = emailError;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSavingDetails(true);
    try {
      const { data } = await api.patch('/profile', { name, email });
      setPartner(data.data);
      toast.success('Profile updated.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setIsSavingDetails(false);
    }
  }

  return (
    <form onSubmit={handleDetailsSubmit} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="profile-name" required>Name</Label>
        <Input
          id="profile-name"
          value={name}
          maxLength={NAME_MAX}
          aria-invalid={!!errors.name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
          }}
          required
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="profile-email" required>Email</Label>
        <Input
          id="profile-email"
          type="email"
          value={email}
          maxLength={EMAIL_MAX}
          aria-invalid={!!errors.email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
          }}
          onBlur={(e) =>
            setErrors((prev) => ({ ...prev, email: e.target.value ? checkEmail(e.target.value) || undefined : undefined }))
          }
          required
        />
        {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
      </div>
      <Button type="submit" variant="accent" disabled={isSavingDetails}>
        {isSavingDetails ? 'Saving...' : 'Save details'}
      </Button>
    </form>
  );
}

type PasswordErrors = Partial<Record<'current' | 'new' | 'confirm', string>>;

function PasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<PasswordErrors>({});
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  function clearError(field: keyof PasswordErrors) {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();

    // current_password and password are required by the API, password with a
    // minimum of 8. The confirmation is a client-side check only.
    const nextErrors: PasswordErrors = {};
    if (!currentPassword) nextErrors.current = 'Current password is required.';
    if (!newPassword) nextErrors.new = 'New password is required.';
    else if (newPassword.length < PASSWORD_MIN) {
      nextErrors.new = `New password must be at least ${PASSWORD_MIN} characters.`;
    }
    if (!confirmPassword) nextErrors.confirm = 'Confirm new password is required.';
    else if (newPassword !== confirmPassword) nextErrors.confirm = 'New password and confirm password do not match.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSavingPassword(true);
    try {
      await api.patch('/password', { current_password: currentPassword, password: newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setIsSavingPassword(false);
    }
  }

  return (
    <form onSubmit={handlePasswordSubmit} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="current-password" required>Current password</Label>
        <Input
          id="current-password"
          type="password"
          value={currentPassword}
          aria-invalid={!!errors.current}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            clearError('current');
          }}
          required
        />
        {errors.current && <p className="text-xs text-destructive">{errors.current}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="new-password" required>New password</Label>
        <Input
          id="new-password"
          type="password"
          value={newPassword}
          aria-invalid={!!errors.new}
          onChange={(e) => {
            setNewPassword(e.target.value);
            clearError('new');
          }}
          minLength={PASSWORD_MIN}
          required
        />
        {errors.new && <p className="text-xs text-destructive">{errors.new}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirm-password" required>Confirm new password</Label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          aria-invalid={!!errors.confirm}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            clearError('confirm');
          }}
          minLength={PASSWORD_MIN}
          required
        />
        {errors.confirm && <p className="text-xs text-destructive">{errors.confirm}</p>}
      </div>
      <Button type="submit" variant="accent" disabled={isSavingPassword}>
        {isSavingPassword ? 'Saving...' : 'Change password'}
      </Button>
    </form>
  );
}
